import { ArrowUturnLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import {useToast} from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { usePublicAssetUploader } from '@/pages/enhanced-content/enhanced-content.queries';

const CustomNodeViewWrapper = (props: any) => {

    const { t } = useTranslation();
    const { toast }  = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [ isAltTextDialogOpen, setIsAltTextDialogOpen ] = useState(false);
    const isEditable = props.editor.isEditable;

    const { mutate: upload, isPending: uploading} = usePublicAssetUploader({
        onError: () => {
            toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
        },
        onSuccess: (data) => {
            props.updateAttributes({
                src: data[0].url
            });
        }
    });

    const handleSetImageButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleSetAltText = (altText: string) => {
        props.updateAttributes({
            alt: altText,
            title: altText,
        });
        setIsAltTextDialogOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let file = null;

        if (e.target.files) {
            file = e.target.files[0];
        }

        if (file) {
            upload({
                file
            });
        }
    };

    const handleResetButtonClick = () => {
        props.updateAttributes({
            src: props.node.type.defaultAttrs.src,
            alt: props.node.type.defaultAttrs.alt,
            title: props.node.type.defaultAttrs.title,
        });
    };

    return (
        <NodeViewWrapper className="tw-relative" contentEditable="false">
            {isEditable &&
            <div
                className={`tw-absolute tw-cursor-pointer tw-rounded-sm tw-text-white tw-px-1 tw-py-1
                ${props.node.attrs.alt ? 'tw-bg-green-500' : 'tw-bg-red-500'}
                tw-flex tw-items-center tw-justify-center tw-h-6`}
                style={{
                    top: 10,
                    right: 67,
                }}
                contentEditable="false"
                onClick={() => setIsAltTextDialogOpen(true)}
                title={props.node.attrs.alt ?
                    t('ENHANCED_CONTENT.IMAGE_ALT_TEXT_IS_SET') : t('ENHANCED_CONTENT.IMAGE_ALT_TEXT_IS_NOT_SET')}
            >
                {
                    props.node.attrs.alt ?
                        <Icon
                            icon={CheckCircleIcon}
                            size='sm'
                            className="tw-text-white tw-opacity-100"
                        />
                        :
                        <Icon
                            icon={ExclamationTriangleIcon}
                            size='sm'
                            className="tw-text-white tw-opacity-100"
                        />
                }
            </div>}

            {(isEditable && isAltTextDialogOpen) &&
            <AltTextDialog
                open={isAltTextDialogOpen}
                onSetAltText={handleSetAltText}
                altText={props.node.attrs.alt}
                onClose={() => setIsAltTextDialogOpen(false) }
            />
            }

            {isEditable &&
            <div
                className="tw-absolute tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-h-6"
                style={{
                    top: 8,
                    right: 37
                }}
                contentEditable="false"
                onClick={handleResetButtonClick}
            >
                <Icon
                    icon={ArrowUturnLeftIcon}
                    size='sm'
                    className="tw-text-gray-400"
                />
            </div>}

            {isEditable && (
                !uploading ?
                    <div
                        className="tw-absolute tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-h-6"
                        style={{
                            top: 10,
                            right: 8
                        }}
                        contentEditable="false"
                        onClick={handleSetImageButtonClick}
                    >
                        <Icon
                            icon={PhotoIcon}
                            size='sm'
                            className="tw-text-gray-400"
                        />
                    </div>
                    :
                    <div
                        className="tw-absolute tw-flex tw-items-center tw-justify-center"
                        style={{
                            width: 18,
                            height: 18,
                            top: 8,
                            right: 8
                        }}>
                        <Loader />
                    </div>
            )}

            {isEditable && <input ref={fileInputRef} type="file" accept="image/" style={{ display: 'none' }} onChange={handleInputChange} />}

            <img width="100%" title={props.node.attrs.title} alt={props.node.attrs.alt} src={props.node.attrs.src} />
        </NodeViewWrapper>
    );
};

const AltTextDialog = ({ open, altText, onSetAltText, onClose }:
    { open: boolean, altText: string, onSetAltText: (altText: string) => void, onClose: () => void }) => {

    const { t } = useTranslation();
    const [ internalAltText, setInternalAltText ] = useState<string | undefined>(altText === '' ? undefined : altText);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault>{t('ENHANCED_CONTENT.IMAGE_ALT_TEXT')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none" className="tw-flex tw-px-6 tw-py-3">
                    <Input placeholder={t('ENHANCED_CONTENT.IMAGE_ALT_TEXT')}
                        value={internalAltText} onChange={e => setInternalAltText(e.target.value)} />
                </DialogBody>
                <DialogFooter>
                    <Button variant="default" type="submit"
                        disabled={internalAltText === undefined}
                        onClick={() => internalAltText && onSetAltText(internalAltText)}>
                        {t('ENHANCED_CONTENT.SET_IMAGE_ALT_TEXT')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const ImageNode = Node.create({
    name: 'image',
    draggable: true,
    selectable: false,
    isolating: true,

    addOptions() {
        return {
            inline: false,
            allowBase64: false,
            HTMLAttributes: {},
        };
    },

    inline() {
        return this.options.inline;
    },

    group() {
        return this.options.inline ? 'inline' : 'block';
    },

    addAttributes() {
        return {
            src: {
                default: 'https://placehold.co/600x400',
            },
            alt: {
                default: '',
            },
            title: {
                default: '',
            },
            width: {
                default: '100%'
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'img'
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'img',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(CustomNodeViewWrapper);
    }
});

export default ImageNode;
